import React, { useState, useRef, useEffect } from 'react';
import { useLabelStore } from '../store/useLabelStore';

// Bornes RÉELLES du zoom du canevas : LabelCanvas.jsx borne la molette de 0,1 à 100,
// soit de 10 % à 10 000 %. Le curseur du navigateur était borné à max 5 : au-delà de
// 500 % il restait collé à sa butée et n'informait plus de rien, et le moindre
// contact avec lui ramenait brutalement le zoom dans la plage 0,1 à 5.
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 100;
// Le curseur est LOGARITHMIQUE : sur une échelle linéaire de 0,1 à 100, toute la
// plage de travail utile (10 % à 200 %) tiendrait dans les deux premiers pour cent
// de la course, impossible à régler à la souris. En logarithmique, chaque doublement
// du zoom occupe la même longueur de course. 1000 crans pour un déplacement fluide.
const SLIDER_STEPS = 1000;
const clampZoom = (s) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number(s) || 1));

export default function FloatingNavigator() {
    const { canvas, setCanvas, elements } = useLabelStore();
    const [isExpanded, setIsExpanded] = useState(false);
    const containerRef = useRef(null);
    const [viewportSize, setViewportSize] = useState({ width: 800, height: 600 });
    
    useEffect(() => {
        const checkViewport = () => {
            const el = document.getElementById("canvas-viewport");
            if (el) {
                setViewportSize({ width: el.clientWidth, height: el.clientHeight });
            }
        };
        checkViewport();
        window.addEventListener('resize', checkViewport);
        const interval = setInterval(checkViewport, 1000);
        return () => {
            window.removeEventListener('resize', checkViewport);
            clearInterval(interval);
        };
    }, []);

    const zoomOut = () => setCanvas({ scale: Math.max(ZOOM_MIN, canvas.scale - 0.5) });
    const zoomIn = () => setCanvas({ scale: Math.min(ZOOM_MAX, canvas.scale + 0.5) });
    const zoomFit = () => setCanvas({ scale: 1 });

    // Position du curseur (0 à 1000) pour le zoom courant, et l'inverse.
    const sliderPos = Math.round(
        (SLIDER_STEPS * Math.log(clampZoom(canvas.scale) / ZOOM_MIN)) / Math.log(ZOOM_MAX / ZOOM_MIN)
    );
    const posToScale = (pos) => {
        const s = ZOOM_MIN * Math.pow(ZOOM_MAX / ZOOM_MIN, pos / SLIDER_STEPS);
        // Quatre chiffres SIGNIFICATIFS, et non deux décimales : l'échelle est
        // logarithmique, un pas vaut 0,0007 près du minimum et 0,7 près du
        // maximum. Arrondir à deux décimales écrasait donc des centaines de
        // positions sur la même valeur — mesuré : 252 des 1001 positions ne
        // revenaient pas à elles-mêmes, sur toute la plage de travail, et la
        // poignée sautait au relâché. La précision relative supprime aussi bien
        // les 1,0000000002 que ce défaut d'aller-retour.
        return clampZoom(Number(s.toPrecision(4)));
    };

    const navWidth = 240;
    const navHeight = 160;

    const docAspect = canvas.width_mm / canvas.height_mm;
    const navAspect = navWidth / navHeight;
    
    let drawWidth, drawHeight;
    if (docAspect > navAspect) {
        drawWidth = navWidth - 20;
        drawHeight = drawWidth / docAspect;
    } else {
        drawHeight = navHeight - 20;
        drawWidth = drawHeight * docAspect;
    }

    const scaleFactor = drawWidth / canvas.width_mm;

    const visibleWidth = viewportSize.width / canvas.scale;
    const visibleHeight = viewportSize.height / canvas.scale;
    
    const centerX = canvas.width_mm / 2 - (canvas.panX || 0) / canvas.scale;
    const centerY = canvas.height_mm / 2 - (canvas.panY || 0) / canvas.scale;

    const viewBoxW = visibleWidth * scaleFactor;
    const viewBoxH = visibleHeight * scaleFactor;
    const viewBoxX = (centerX * scaleFactor) - (viewBoxW / 2);
    const viewBoxY = (centerY * scaleFactor) - (viewBoxH / 2);

    return (
        <div className="absolute top-6 right-6 z-[100] flex flex-col items-end gap-3 font-mono">
            {/* Floating Navigation Button */}
            <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className={`w-12 h-12 rounded-full flex items-center justify-center border-2 shadow-xl transition-all hover:scale-105 hover:shadow-forge-accent/20 hover:border-white ${isExpanded ? 'bg-white text-black border-white' : 'bg-white/5 border-white/10 text-white/40 focus:outline-none'}`}
                title="Navigateur & Zoom" aria-label="Navigateur & Zoom"
            >
                {/* Compass / Navigation Wheel Icon */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"></polygon>
                </svg>
            </button>

            {/* Floating Panel Box */}
            {/* Le fond du panneau empilait deux opacités : aucune règle n'était
                produite et le panneau flottait entièrement transparent au-dessus du
                canevas, donc illisible. Les utilitaires d'animation, eux, venaient
                d'un plugin absent du projet : ils ne jouaient rien. */}
            {isExpanded && (
                <div className="w-[280px] bg-forge-surface/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl p-4 flex flex-col gap-4 origin-top-right">
                    <div className="flex items-center justify-between">
                        <span className="text-xs uppercase font-bold text-white tracking-wider">Navigateur</span>
                        <button onClick={() => setIsExpanded(false)} className="text-white/40 hover:text-white px-2 py-1 transition-colors">✕</button>
                    </div>

                    {/* The Mini-Map */}
                    <div ref={containerRef} className="bg-black/20 border border-white/10 rounded-lg overflow-hidden flex items-center justify-center relative shadow-inner" style={{ height: `${navHeight}px` }}>
                        <div 
                            className="bg-white shadow-sm relative overflow-hidden" 
                            style={{ 
                                width: `${drawWidth}px`, 
                                height: `${drawHeight}px`,
                                backgroundColor: canvas.color_bg || '#FFFFFF'
                            }}
                        >
                            {elements.map(el => (
                                <div 
                                    key={el.id}
                                    className="absolute bg-forge-accent/40 border border-white/60"
                                    style={{
                                        left: `${el.x * scaleFactor}px`,
                                        top: `${el.y * scaleFactor}px`,
                                        width: `${el.width * scaleFactor}px`,
                                        height: `${el.height * scaleFactor}px`,
                                        transform: `rotate(${el.rotation || 0}deg)`
                                    }}
                                />
                            ))}

                            {/* Viewfinder Outline */}
                            <div 
                                className="absolute border-[1.5px] border-red-500 bg-red-500/10 pointer-events-none transition-all duration-75"
                                style={{
                                    left: `${Math.max(0, viewBoxX)}px`,
                                    top: `${Math.max(0, viewBoxY)}px`,
                                    width: `${Math.min(drawWidth, viewBoxW)}px`,
                                    height: `${Math.min(drawHeight, viewBoxH)}px`,
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    {/* Zoom Controls */}
                    <div className="flex items-center gap-2 mt-1 bg-black/20 p-2 rounded-lg border border-white/10">
                        <button onClick={zoomOut} className="w-8 h-8 rounded-md bg-white/5 hover:bg-white hover:text-black border border-white/10 text-white flex items-center justify-center font-bold transition-colors" title="Dézoomer" aria-label="Dézoomer">-</button>
                        
                        <div className="flex-1 flex flex-col items-center">
                            <input
                                type="range"
                                min="0" max={SLIDER_STEPS} step="1"
                                value={sliderPos}
                                onChange={(e) => setCanvas({ scale: posToScale(Number(e.target.value)) })}
                                className="w-full accent-white cursor-pointer"
                                title="Niveau de zoom" aria-label="Niveau de zoom"
                                aria-valuetext={`${Math.round(canvas.scale * 100)} %`}
                            />
                            <div className="flex justify-between w-full mt-2 px-1">
                                {/* Ce contrôle n'ajuste rien à la fenêtre : il repose le zoom
                                    à 100 % (`scale = 1`). Son libellé dit donc ce qu'il fait.
                                    Et c'est un vrai bouton, comme les « - » et « + » voisins :
                                    en <span> il était hors d'atteinte au clavier. */}
                                <button
                                    type="button"
                                    onClick={zoomFit}
                                    title="Revenir au zoom 100 %" aria-label="Revenir au zoom 100 %"
                                    className="text-[10px] text-white/40 font-bold select-none hover:text-white uppercase transition-colors rounded focus:outline-none focus-visible:ring-1 focus-visible:ring-white/60"
                                >
                                    Zoom 100 %
                                </button>
                                <span className="text-[10px] text-white font-bold select-none">{Math.round(canvas.scale * 100)} %</span>
                            </div>
                        </div>

                        <button onClick={zoomIn} className="w-8 h-8 rounded-md bg-white/5 hover:bg-white hover:text-black border border-white/10 text-white flex items-center justify-center font-bold transition-colors" title="Zoomer" aria-label="Zoomer">+</button>
                    </div>
                </div>
            )}
        </div>
    );
}
